import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useCheckout from '@jwp/ott-hooks-react/src/useCheckout';
import { modalURLFromLocation, modalURLFromWindowLocation } from '@jwp/ott-ui-react/src/utils/location';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { FormValidationError } from '@jwp/ott-common/src/errors/FormValidationError';
import { useTranslation } from 'react-i18next';
import { createURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { findDefaultCardMethodId } from '@jwp/ott-common/src/utils/payments';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';

import CheckoutForm from '../../../components/CheckoutForm/CheckoutForm';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import PayPal from '../../../components/PayPal/PayPal';
import NoPaymentRequired from '../../../components/NoPaymentRequired/NoPaymentRequired';
import PaymentForm, { type PaymentFormData } from '../../../components/PaymentForm/PaymentForm';
import AdyenInitialPayment from '../../AdyenInitialPayment/AdyenInitialPayment';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import useRecaptcha from '../../../hooks/useRecaptcha';
import useQueryParam from '../../../hooks/useQueryParam';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adyenUpdating, setAdyenUpdating] = useState(false); // @todo: integrate AdyenInitialPayment into useCheckout
  const couponCodeParam = useQueryParam('coupon');

  const [couponFormOpen, setCouponFormOpen] = useState(!!couponCodeParam);
  const [showCouponCodeSuccess, setShowCouponCodeSuccess] = useState(false);
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();

  const chooseOfferUrl = modalURLFromLocation(location, 'choose-offer');
  const welcomeUrl = modalURLFromLocation(location, 'welcome');
  const closeModalUrl = modalURLFromLocation(location, null);

  const { recaptchaRef, captchaSiteKey, getCaptchaValue } = useRecaptcha();

  const backButtonClickHandler = () => navigate(chooseOfferUrl);

  const { selectedOffer, offerType, paymentMethods, order, isSubmitting, updateOrder, submitPaymentWithoutDetails, submitPaymentPaypal, submitPaymentStripe } =
    useCheckout({
      onUpdateOrderSuccess: () => !!couponCode && setShowCouponCodeSuccess(true),
      onSubmitPaymentWithoutDetailsSuccess: () => {
        announce(t('checkout.payment_success'), 'success');

        return navigate(offerType === 'svod' ? welcomeUrl : closeModalUrl, { replace: true });
      },
      onSubmitPaypalPaymentSuccess: ({ redirectUrl }) => {
        window.location.href = redirectUrl;
      },
      onSubmitStripePaymentSuccess: () =>
        navigate(modalURLFromLocation(location, 'waiting-for-payment', { type: selectedOffer?.type, offerId: selectedOffer?.offerId }), { replace: true }),
    });

  const {
    values: { couponCode, paymentMethodId },
    setValue,
    submitting: couponFormSubmitting,
    errors,
    handleChange,
    handleSubmit,
  } = useForm({
    initialValues: { couponCode: couponCodeParam || '', paymentMethodId: findDefaultCardMethodId(paymentMethods) },
    onSubmit: ({ couponCode, paymentMethodId }) => {
      setShowCouponCodeSuccess(false);

      return updateOrder.mutateAsync({ couponCode, paymentMethodId: parseInt(paymentMethodId) });
    },
    onSubmitSuccess: ({ couponCode }): void => setShowCouponCodeSuccess(!!couponCode),
    onSubmitError: ({ error }) => {
      if (error instanceof FormValidationError && error.errors.order?.includes(`Order with id ${order?.id} not found`)) {
        navigate(modalURLFromLocation(location, 'choose-offer'), { replace: true });
      }
    },
  });

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);

    // Always send payment method to backend
    updateOrder.mutateAsync({ couponCode, paymentMethodId: parseInt(event.target.value) });
  };

  const handleAdyenPaymentMethodChange = useEventCallback((paymentMethod: string) => {
    const paymentMethodObject = paymentMethods?.find((method) => method.methodName === paymentMethod);

    if (paymentMethodObject) {
      setValue('paymentMethodId', String(paymentMethodObject.id));
      updateOrder.mutateAsync({ couponCode, paymentMethodId: paymentMethodObject.id });
    }
  });

  useEffect(() => {
    if (!selectedOffer) {
      navigate(chooseOfferUrl, { replace: true });
    }
  }, [navigate, chooseOfferUrl, selectedOffer]);

  // Pre-select first payment method
  useEffect(() => {
    if (!paymentMethods?.length) return;

    setValue('paymentMethodId', findDefaultCardMethodId(paymentMethods));
  }, [paymentMethods, setValue]);

  // Pre-select a deeplinked couponCode
  useEffect(() => {
    if (!couponCodeParam || !order || !paymentMethodId) {
      return;
    }

    if (couponCode !== couponCodeParam) {
      setValue('couponCode', couponCodeParam);
      return;
    }

    if (couponFormSubmitting || order.discount?.type === 'coupon' || errors.couponCode || errors.form) return;

    const parsedPaymentMethodId = Number.parseInt(paymentMethodId);

    if (Number.isNaN(parsedPaymentMethodId)) return;

    handleSubmit();
  }, [couponCodeParam, couponCode, order, paymentMethodId, setValue, couponFormSubmitting, errors.couponCode, errors.form, handleSubmit]);

  // clear after closing the checkout modal
  useEffect(() => {
    return () => setShowCouponCodeSuccess(false);
  }, []);

  // loading state
  if (!selectedOffer || !order || !paymentMethods || !offerType) {
    return (
      <div style={{ height: 300 }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  const cancelUrl = modalURLFromWindowLocation('payment-cancelled');
  const waitingUrl = modalURLFromWindowLocation('waiting-for-payment', { offerId: selectedOffer?.offerId, type: selectedOffer.type });
  const errorUrl = modalURLFromWindowLocation('payment-error');
  const successUrlPaypal = offerType === 'svod' ? waitingUrl : createURL(window.location.href, { u: '' });
  const referrer = window.location.href;

  const paymentMethod = paymentMethods?.find((method) => method.id === parseInt(paymentMethodId));
  const noPaymentRequired = !order?.requiredPaymentDetails;
  const isStripePayment = !noPaymentRequired && paymentMethod?.methodName === 'card' && paymentMethod?.provider === 'stripe';
  const hasAdyenPaymentMethods = !noPaymentRequired && paymentMethods?.some((paymentMethod) => paymentMethod.paymentGateway === 'adyen');
  const isPayPalPayment = !noPaymentRequired && paymentMethod?.methodName === 'paypal';

  const handlePayPalSubmit = async () => {
    const captchaValue = await getCaptchaValue();

    submitPaymentPaypal.mutate({ successUrl: successUrlPaypal, waitingUrl, cancelUrl, errorUrl, couponCode, captchaValue });
  };

  return (
    <CheckoutForm
      order={order}
      offer={selectedOffer}
      offerType={offerType}
      error={errors.form}
      onBackButtonClick={backButtonClickHandler}
      paymentMethods={paymentMethods}
      paymentMethodId={paymentMethodId}
      allowChangingPaymentMethods={!hasAdyenPaymentMethods}
      onPaymentMethodChange={handlePaymentMethodChange}
      onCouponFormSubmit={handleSubmit}
      onCouponInputChange={handleChange}
      onRedeemCouponButtonClick={() => setCouponFormOpen(true)}
      onCloseCouponFormClick={() => setCouponFormOpen(false)}
      couponInputValue={couponCode}
      couponFormOpen={couponFormOpen}
      couponFormApplied={showCouponCodeSuccess}
      couponFormSubmitting={couponFormSubmitting}
      couponFormError={errors.couponCode}
      submitting={isSubmitting || adyenUpdating}
      captchaSiteKey={captchaSiteKey}
      recaptchaRef={recaptchaRef}
    >
      {noPaymentRequired && (
        <NoPaymentRequired
          onSubmit={async () => {
            const captchaValue = await getCaptchaValue();

            return submitPaymentWithoutDetails.mutateAsync({ captchaValue });
          }}
          error={submitPaymentWithoutDetails.error?.message || null}
        />
      )}
      {isStripePayment && (
        <PaymentForm
          onPaymentFormSubmit={async (cardPaymentPayload: PaymentFormData) =>
            await submitPaymentStripe.mutateAsync({ cardPaymentPayload: { ...cardPaymentPayload, couponCode }, referrer, returnUrl: waitingUrl })
          }
        />
      )}
      {hasAdyenPaymentMethods && (
        <>
          <AdyenInitialPayment
            paymentSuccessUrl={offerType === 'svod' ? welcomeUrl : closeModalUrl}
            setUpdatingOrder={setAdyenUpdating}
            onPaymentMethodChange={handleAdyenPaymentMethodChange}
            paymentMethods={paymentMethods || []}
            selectedPaymentMethod={paymentMethod}
            orderId={order.id}
            offerId={order.offerId}
            getCaptchaValue={getCaptchaValue}
            onPayPalSubmit={handlePayPalSubmit}
          />
        </>
      )}
      {!hasAdyenPaymentMethods && isPayPalPayment && <PayPal onSubmit={handlePayPalSubmit} error={submitPaymentPaypal.error?.message || null} />}
    </CheckoutForm>
  );
};

export default Checkout;
